import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { assertNoValidationRejection } from "@/lib/graphql-error";
import { normalizeName } from "@/lib/duplicate-name";
import { userErrorMessage } from "@/lib/user-error";
import type { EditableRefDescriptor, ReadonlyRefDescriptor } from "./reference-data-config";

export interface RefRow {
  id: string;
  label: string;
}

// Shared duplicate guard for add and rename: a case-insensitive label match
// against the existing rows ("Weiblich" == "weiblich"), excluding the row
// currently being renamed. Genesis would accept the duplicate, so the UI
// catches it before the mutation.
// Excludes by id rather than by name so an already-duplicated pair stays flagged: shares
// normalizeName with the dialogs' hasDuplicateName so both agree on what "the same name" is.
export function hasDuplicateLabel(rows: readonly RefRow[], name: string, exceptId?: string) {
  const needle = normalizeName(name);
  return rows.some((row) => row.id !== exceptId && normalizeName(row.label) === needle);
}

interface ReferenceDataListProps {
  // Testanker; mehrere Listen stehen nebeneinander auf einer Seite, deshalb
  // kommt der Namensraum vom Aufrufer (dem Schlüssel der Stammdatenart).
  idPrefix: string;
  title: string;
  sub: string;
  rows: RefRow[];
  loading: boolean;
  emptyText: string;
  busy: boolean;
  error: string | null;
  readOnly?: boolean;
  addPlaceholder?: string;
  onAdd?: (name: string) => void;
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
}

// Presentational reference-data list: collapsible card (default collapsed so
// many lists stay scannable), add field, inline rename, two-step destructive
// delete (@adr SPARK-0011-destructive-actions-require-confirmation@1). Owns only its local UI state; the async work, busy flag
// and error string come from the parent adapter.
function ReferenceDataList({
  idPrefix,
  title,
  sub,
  rows,
  loading,
  emptyText,
  busy,
  error,
  readOnly = false,
  addPlaceholder,
  onAdd,
  onRename,
  onDelete,
}: ReferenceDataListProps) {
  const intl = useIntl();
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<RefRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RefRow | null>(null);

  const draftDuplicate = hasDuplicateLabel(rows, draft.trim());
  const editDuplicate =
    editing !== null && hasDuplicateLabel(rows, editing.label.trim(), editing.id);

  function submitAdd() {
    const name = draft.trim();
    if (!name || !onAdd || hasDuplicateLabel(rows, name)) return;
    onAdd(name);
    setDraft("");
  }

  function submitRename() {
    if (!editing) return;
    const name = editing.label.trim();
    if (!name || hasDuplicateLabel(rows, name, editing.id)) return;
    if (onRename) onRename(editing.id, name);
    setEditing(null);
  }

  return (
    <>
      {/* These lists sit directly on the page (not nested on a surface panel
          like in Match), so give them the porcelain surface fill to read as
          cards — matching the other Settings panels. */}
      <Disclosure id={idPrefix} title={title} count={rows.length > 0 ? rows.length : null} className="bg-surface">
      <div className="text-smycles-meta text-ink-muted">{sub}</div>

      {!readOnly && onAdd ? (
        <>
          <div className="mt-4 flex items-center gap-2">
            <input
              id={`${idPrefix}-addinput`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitAdd();
              }}
              placeholder={addPlaceholder}
              disabled={busy}
              className="bg-surface flex-1 rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
            />
            <Button
              id={`${idPrefix}-add`}
              variant="primary"
              size="compact"
              onClick={submitAdd}
              disabled={busy || draft.trim().length === 0 || draftDuplicate}
            >
              {intl.formatMessage({ id: "referenceData.add", defaultMessage: "Add" })}
            </Button>
          </div>
          {draftDuplicate ? (
            <div id={`${idPrefix}-addduplicate`} className="mt-2 text-smycles-meta text-ink-muted">
              {intl.formatMessage({
                id: "referenceData.duplicate",
                defaultMessage: "This entry already exists.",
              })}
            </div>
          ) : null}
        </>
      ) : null}

      {error ? (
        <div id={`${idPrefix}-error`} className="mt-3 rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">
          {error}
        </div>
      ) : null}

      <div className="mt-4 divide-y divide-ink/5">
        {loading && rows.length === 0 ? (
          <div className="py-3 text-smycles-meta text-ink-muted">
            {intl.formatMessage({ id: "referenceData.loading", defaultMessage: "Loading…" })}
          </div>
        ) : rows.length === 0 ? (
          <div id={`${idPrefix}-empty`} className="py-3 text-smycles-meta text-ink-muted">{emptyText}</div>
        ) : (
          rows.map((row) => {
            const isEditing = editing?.id === row.id;
            const editLabel = intl.formatMessage({ id: "referenceData.edit", defaultMessage: "Edit" });
            const deleteLabel = intl.formatMessage({ id: "referenceData.delete", defaultMessage: "Delete" });
            return (
              <div
                key={row.id}
                id={`${idPrefix}-row-${row.id}`}
                className="grid min-h-11 grid-cols-1 items-center gap-2 py-2 @sm:grid-cols-[minmax(0,1fr)_auto] @sm:gap-3"
              >
                {isEditing ? (
                  <>
                    <input
                      id={`${idPrefix}-row-${row.id}-input`}
                      autoFocus
                      value={editing.label}
                      onChange={(e) => setEditing({ id: row.id, label: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submitRename();
                        if (e.key === "Escape") setEditing(null);
                      }}
                      disabled={busy}
                      className="bg-surface flex-1 rounded-md px-3 py-1.5 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
                    />
                    <div className="flex w-full shrink-0 items-center justify-start gap-2 @sm:w-auto @sm:justify-end">
                      <Button
                        id={`${idPrefix}-row-${row.id}-save`}
                        variant="primary"
                        size="compact"
                        onClick={submitRename}
                        disabled={busy || editDuplicate}
                      >
                        {intl.formatMessage({ id: "referenceData.save", defaultMessage: "Save" })}
                      </Button>
                      <Button id={`${idPrefix}-row-${row.id}-cancel`} variant="secondary" size="compact" onClick={() => setEditing(null)} disabled={busy}>
                        {intl.formatMessage({ id: "referenceData.cancel", defaultMessage: "Cancel" })}
                      </Button>
                    </div>
                    {editDuplicate ? (
                      <div id={`${idPrefix}-row-${row.id}-duplicate`} className="text-smycles-meta text-ink-muted @sm:col-span-2">
                        {intl.formatMessage({
                          id: "referenceData.duplicate",
                          defaultMessage: "This entry already exists.",
                        })}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div id={`${idPrefix}-row-${row.id}-name`} className="min-w-0 text-smycles-body font-medium">
                      {row.label}
                    </div>
                    <div className="flex w-full shrink-0 items-center justify-start gap-1 @sm:w-auto @sm:min-w-[11rem] @sm:justify-end">
                      {!readOnly && onRename ? (
                        <Button
                          id={`${idPrefix}-row-${row.id}-edit`}
                          variant="ghost"
                          size="sm"
                          className="min-w-[5.5rem]"
                          onClick={() => setEditing(row)}
                          disabled={busy}
                          title={editLabel}
                          aria-label={editLabel}
                        >
                          {editLabel}
                        </Button>
                      ) : null}
                      {!readOnly && onDelete ? (
                        <Button
                          id={`${idPrefix}-row-${row.id}-delete`}
                          variant="ghost"
                          size="sm"
                          className="min-w-[4.75rem]"
                          onClick={() => setPendingDelete(row)}
                          disabled={busy}
                          title={deleteLabel}
                          aria-label={deleteLabel}
                        >
                          {deleteLabel}
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      </Disclosure>
      <ConfirmDialog
        id={`${idPrefix}-deletedialog`}
        open={pendingDelete !== null}
        title={intl.formatMessage(
          { id: "referenceData.confirmDelete.title", defaultMessage: "Delete “{label}”?" },
          { label: pendingDelete?.label ?? "" },
        )}
        message={intl.formatMessage({
          id: "referenceData.confirmDelete.message",
          defaultMessage:
            "The entry is removed for good and will no longer be offered in profiles and Match requirements.",
        })}
        confirmLabel={intl.formatMessage({ id: "referenceData.delete", defaultMessage: "Delete" })}
        onConfirm={() => {
          if (pendingDelete && onDelete) onDelete(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}

// Apollo adapter for an editable reference-data type. Owns busy/error and the
// refetch; the descriptor owns the Genesis write-input shape.
export function EditableReferenceData({ descriptor }: { descriptor: EditableRefDescriptor }) {
  const intl = useIntl();
  const { data, loading, refetch } = useQuery(descriptor.listDoc);
  const [createMutation] = useMutation(descriptor.createDoc);
  const [updateMutation] = useMutation(descriptor.updateDoc);
  const [deleteMutation] = useMutation(descriptor.deleteDoc);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = data ? descriptor.selectRows(data) : [];

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      const res = await work();
      // Every reference-data create/update returns a union whose Validate member the documents now
      // select. Without this the row simply never appeared and the admin was told nothing: genesis
      // resolves the mutation, so there is no error for the catch below to see.
      assertNoValidationRejection(
        res,
        intl.formatMessage({ id: "admin.referenceData.saveFailed", defaultMessage: "The entry could not be saved." }),
      );
      await refetch();
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ReferenceDataList
      idPrefix={`refdata-${descriptor.key}`}
      title={intl.formatMessage({ id: descriptor.labelId })}
      sub={intl.formatMessage({ id: descriptor.subId })}
      addPlaceholder={intl.formatMessage({ id: descriptor.addPlaceholderId })}
      emptyText={intl.formatMessage({ id: descriptor.emptyId })}
      rows={rows}
      loading={loading}
      busy={busy}
      error={error}
      onAdd={(name) =>
        void run(() => createMutation({ variables: descriptor.createVariables(name) }))
      }
      onRename={(id, name) =>
        void run(() => updateMutation({ variables: descriptor.updateVariables(id, name) }))
      }
      onDelete={(id) => void run(() => deleteMutation({ variables: { input: { id } } }))}
    />
  );
}

// Apollo adapter for a read-only reference-data type (no backing mutations).
export function ReadonlyReferenceData({ descriptor }: { descriptor: ReadonlyRefDescriptor }) {
  const intl = useIntl();
  const { data, loading } = useQuery(descriptor.listDoc);
  const rows = data ? descriptor.selectRows(data) : [];
  return (
    <ReferenceDataList
      readOnly
      idPrefix={`refdata-${descriptor.key}`}
      title={intl.formatMessage({ id: descriptor.labelId })}
      sub={intl.formatMessage({ id: descriptor.subId })}
      emptyText={intl.formatMessage({ id: descriptor.emptyId })}
      rows={rows}
      loading={loading}
      busy={false}
      error={null}
    />
  );
}
